import { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Mail, MapPin, Phone } from "lucide-react";
import { LogoFull } from "@/components/Logo";
import { businessLines } from "@/data/businessLines";
import { api } from "@/lib/api";
import { payoff } from "@/data/content";
import { toast } from "sonner";

export function Footer() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Inserisci un'email valida");
      return;
    }
    try {
      await api.post("/newsletter", { email });
      setSent(true);
      setEmail("");
      toast.success("Iscrizione confermata. Niente spam, promesso.");
    } catch {
      toast.error("Si è verificato un errore. Riprova.");
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[#091623] border-t border-white/10 relative noise-overlay">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 relative z-10">
        <div className="grid gap-12 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <LogoFull />
            <p className="font-serif-claim italic text-[#C5A059] text-lg mt-5">{payoff}</p>
            <div className="flex items-center gap-4 mt-6">
              <a
                href="https://www.linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin"
                className="w-9 h-9 border border-white/15 flex items-center justify-center text-gray-300 hover:text-[#C5A059] hover:border-[#C5A059] transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={17} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div>
            <div className="label-eyebrow mb-4">Metodo &amp; Servizi</div>
            <ul className="space-y-2">
              {businessLines.slice(0, 8).map((b) => (
                <li key={b.slug}>
                  <Link
                    to={`/linee/${b.slug}`}
                    className="text-sm text-gray-400 hover:text-[#C5A059] transition-colors"
                  >
                    {b.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="label-eyebrow mb-4">Navigazione</div>
            <ul className="space-y-2">
              {[
                ["/chi-siamo", "Chi Siamo"],
                ["/casi-studio", "Casi Studio"],
                ["/investitori", "Per gli Investitori"],
                ["/proprietari", "Proprietari in difficoltà"],
                ["/blog", "Risorse & Blog"],
                ["/contatti", "Contatti"],
                ["/privacy", "Privacy Policy"],
                ["/cookie", "Cookie Policy"],
              ].map(([to, label]) => (
                <li key={to}>
                  <Link to={to} className="text-sm text-gray-400 hover:text-[#C5A059] transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="label-eyebrow mb-4">Contatti</div>
            <ul className="space-y-3 text-sm text-gray-400">
              <li className="flex items-start gap-2">
                <MapPin size={16} className="text-[#C5A059] mt-0.5" strokeWidth={1.5} />
                SS12 Abetone e Brennero KM 5+812 n. 7/C, 56017 San Giuliano Terme (PI)
              </li>
              <li className="flex items-center gap-2">
                <Phone size={16} className="text-[#C5A059]" strokeWidth={1.5} />
                <a href="tel:+393394641508" className="hover:text-[#C5A059] transition-colors">+39 339 464 1508</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={16} className="text-[#C5A059]" strokeWidth={1.5} />
                info@operapartners.it
              </li>
            </ul>

            <div className="label-eyebrow mt-8 mb-3">Newsletter</div>
            <form onSubmit={subscribe} data-testid="newsletter-form" className="flex">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="La tua email"
                data-testid="newsletter-email"
                className="flex-1 bg-[#1E2229] border border-white/10 text-white placeholder:text-white/30 px-3 h-11 text-sm focus:outline-none focus:border-[#C5A059]"
              />
              <button
                type="submit"
                data-testid="newsletter-submit"
                className="bg-[#C5A059] text-[#0D1B2A] px-4 h-11 text-sm font-medium hover:bg-[#D8B978] transition-colors"
              >
                {sent ? "Fatto" : "Iscriviti"}
              </button>
            </form>
            <p className="text-xs text-gray-500 mt-2">
              Aggiornamenti su opportunità e normativa. Niente spam.
            </p>
          </div>
        </div>

        {/* LEGAL / COMPLIANCE */}
        <div className="border-t border-white/10 mt-14 pt-8 space-y-6">
          <p className="text-xs text-gray-400 leading-relaxed max-w-4xl">
            OPERA Real Estate Partners è un'iniziativa di consulenza e valorizzazione immobiliare
            strategica gestita da Nicola Micheletti &amp; Partners.
          </p>
          <p className="text-xs text-gray-500 leading-relaxed max-w-4xl">
            <strong className="text-gray-300">Nota di trasparenza:</strong> Le attività svolte
            consistono in analisi di fattibilità economico-finanziaria, advisory strategica, project
            management e valorizzazione di patrimoni immobiliari. Ai sensi della Legge 39/1989 e
            succ. mod., ogni eventuale attività di mediazione immobiliare e rogito notarile è svolta
            esclusivamente in collaborazione con agenzie immobiliari abilitate e pubblici ufficiali
            delegati.
          </p>

          <div className="text-xs text-gray-500 leading-relaxed">
            <p className="text-gray-200 font-display tracking-wide">OPERA Real Estate Partners</p>
            <p>Un'iniziativa promossa da SALEN S.R.L.</p>
            <p>Sede Legale: SS12 Abetone e Brennero KM 5+812 n. 7/C, 56017 San Giuliano Terme (PI)</p>
            <p>C.F. / P.IVA e Iscr. Reg. Imprese Toscana Nord-Ovest: 02353510502 | REA: PI-200840</p>
            <p>Capitale Sociale: € 75.000,00 i.v. | PEC: salensrl@pec.it</p>
          </div>

          <p className="text-xs text-gray-500 leading-relaxed max-w-4xl border-t border-white/5 pt-6">
            <strong className="text-gray-400">Disclaimer di conformità:</strong> Le attività svolte
            da OPERA Real Estate Partners consistono esclusivamente in advisory strategica, analisi
            economico-finanziaria, due diligence e gestione di progetti di riqualificazione
            patrimoniale. Ai sensi e per gli effetti della Legge n. 39/1989 e successive modifiche,
            le attività di mediazione immobiliare e la stipula di atti vincolanti sono demandate in
            via esclusiva ad agenzie abilitate e pubblici ufficiali delegati.
          </p>

          <div className="flex flex-col md:flex-row justify-between gap-3 text-xs text-gray-500 pt-2">
            <p>© {new Date().getFullYear()} SALEN S.R.L. — Tutti i diritti riservati</p>
            <p className="flex gap-4">
              <Link to="/privacy" className="hover:text-[#C5A059]">Privacy</Link>
              <Link to="/cookie" className="hover:text-[#C5A059]">Cookie</Link>
              <Link to="/login" data-testid="footer-login" className="hover:text-[#C5A059]">Area riservata</Link>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
